import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { useLocation, useNavigate, Link } from "react-router-dom";

const VendorTransactions = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const vendor = location.state?.vendor;
  const server_url = localStorage.getItem("server_url");
  const company_id = localStorage.getItem("company_id") || "2";
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const grage_id = userData.grage_id || "1";

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [currentOutstanding, setCurrentOutstanding] = useState(0);

  // Date filter state - last 50 records by default
  const [dateFilters, setDateFilters] = useState({
    from_date: "",
    to_date: new Date().toISOString().split("T")[0],
  });

  // Form state
  const [formData, setFormData] = useState({
    vendor_id: vendor?.vendor_id || "",
    amount: "",
    transaction_type: "2", // Static 2 = OUT for vendor
    transaction_date: new Date().toISOString().split("T")[0],
    remark: "",
    available_balance: 0,
  });

  // State for delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    transactionId: null,
    transactionAmount: 0,
  });

  // Use refs to track component mounting
  const isMounted = useRef(true);

  // Calculate default from date (for last 50 records)
  const calculateDefaultFromDate = async () => {
    if (!vendor) return;

    try {
      // First get total transaction count
      const countFormData = new FormData();
      countFormData.append("company_id", company_id);
      countFormData.append("vendor_id", vendor.vendor_id);
      countFormData.append("from_date", "2000-01-01");
      countFormData.append("to_date", new Date().toISOString().split("T")[0]);
      countFormData.append("action", "view_transactions");
      countFormData.append("transaction_grage_id", grage_id);

      const response = await axios.post(
        `${server_url}transaction.php`,
        countFormData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (response.data.status == "1" && response.data.result) {
        const allTransactions = response.data.result;
        if (allTransactions.length > 0) {
          // Get 50th transaction from the end (since sorted DESC)
          const index = Math.max(0, allTransactions.length - 50);
          const targetTransaction = allTransactions[index];

          if (targetTransaction && targetTransaction.transaction_date) {
            setDateFilters((prev) => ({
              ...prev,
              from_date: targetTransaction.transaction_date,
            }));
          } else {
            // If no 50th transaction, use first transaction date
            setDateFilters((prev) => ({
              ...prev,
              from_date:
                allTransactions[allTransactions.length - 1].transaction_date,
            }));
          }
        } else {
          // No transactions, use 30 days ago
          const thirtyDaysAgo = new Date();
          thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
          setDateFilters((prev) => ({
            ...prev,
            from_date: thirtyDaysAgo.toISOString().split("T")[0],
          }));
        }
      }
    } catch (error) {
      console.error("Error calculating from date:", error);
      // Fallback to 30 days ago
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      setDateFilters((prev) => ({
        ...prev,
        from_date: thirtyDaysAgo.toISOString().split("T")[0],
      }));
    }
  };

  useEffect(() => {
    isMounted.current = true;

    if (vendor) {
      setFormData((prev) => ({
        ...prev,
        vendor_id: vendor.vendor_id,
      }));
      setCurrentOutstanding(parseFloat(vendor.vendor_outstanding || 0));

      // Calculate default from date and then load transactions
      calculateDefaultFromDate().then(() => {
        loadTransactions();
      });
    }

    return () => {
      isMounted.current = false;
    };
  }, [vendor]);

  const loadTransactions = async () => {
    if (!vendor || !isMounted.current) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("vendor_id", vendor.vendor_id);
      formData.append("from_date", dateFilters.from_date);
      formData.append("to_date", dateFilters.to_date);
      formData.append("action", "view_transactions");
      formData.append("transaction_grage_id", grage_id);

      const response = await axios.post(
        `${server_url}transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (isMounted.current && response.data.status == "1") {
        setTransactions(response.data.result || []);
      } else if (isMounted.current && response.data.status !== "1") {
        toast.error(response.data.error || "Failed to load transactions");
        setTransactions([]);
      }
    } catch (error) {
      console.error("Error loading transactions:", error);
      if (isMounted.current) {
        toast.error("Unable to load transactions");
        setTransactions([]);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "amount") {
      const amount = parseFloat(value) || 0;
      // For vendor: OUT transaction increases outstanding (we pay vendor)
      const newBalance = currentOutstanding + amount;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
        available_balance: newBalance,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilter = () => {
    if (!dateFilters.from_date || !dateFilters.to_date) {
      toast.error("Please select both start and end dates");
      return;
    }
    loadTransactions();
  };

  const openConfirmPopup = () => {
    if (!formData.amount || !formData.transaction_date) {
      toast.error("Please fill in all required fields");
      return;
    }
    setShowConfirmPopup(true);
  };

  const closeConfirmPopup = () => {
    setShowConfirmPopup(false);
  };

  const handleSubmit = async () => {
    if (!formData.amount || !formData.transaction_date || !isMounted.current) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      const postData = new FormData();
      postData.append("company_id", company_id);
      postData.append("vendor_id", formData.vendor_id);
      postData.append("amount", formData.amount);
      postData.append("remark", formData.remark);
      postData.append("transaction_date", formData.transaction_date);
      postData.append("transaction_type", formData.transaction_type);
      postData.append("available_balance", formData.available_balance);
      postData.append("transaction_grage_id", grage_id);
      postData.append("expense_category_id", "");
      postData.append("action", "add_transaction");

      const response = await axios.post(
        `${server_url}transaction.php`,
        postData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (isMounted.current && response.data.status == "1") {
        toast.success("Transaction added successfully!");

        // Reset form
        setFormData({
          vendor_id: vendor?.vendor_id || "",
          amount: "",
          transaction_type: "2",
          transaction_date: new Date().toISOString().split("T")[0],
          remark: "",
          available_balance: 0,
        });

        // Reload transactions
        await loadTransactions();

        // Update current outstanding
        setCurrentOutstanding(parseFloat(formData.available_balance));
      } else if (isMounted.current) {
        toast.error(response.data.error || "Failed to add transaction");
      }
    } catch (error) {
      console.error("Error adding transaction:", error);
      if (isMounted.current) {
        toast.error("Unable to add transaction");
      }
    } finally {
      if (isMounted.current) {
        setSubmitting(false);
        setShowConfirmPopup(false);
      }
    }
  };

  const openDeleteConfirm = (transactionId, amount) => {
    setDeleteConfirm({
      isOpen: true,
      transactionId,
      transactionAmount: amount,
    });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({
      isOpen: false,
      transactionId: null,
      transactionAmount: 0,
    });
  };

  const handleDelete = async () => {
    if (!deleteConfirm.transactionId || !isMounted.current) return;

    try {
      const formData = new FormData();
      formData.append("company_id", company_id);
      formData.append("transaction_grage_id", grage_id);
      formData.append("transaction_id", deleteConfirm.transactionId);
      formData.append("action", "delete_transaction_with_rebalance");

      const response = await axios.post(
        `${server_url}transaction.php`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (isMounted.current && response.data.status == "1") {
        toast.success("Transaction deleted successfully!");

        // Update local state
        setTransactions((prev) =>
          prev.filter((t) => t.transaction_id !== deleteConfirm.transactionId)
        );
        // Go back to vendors if no transactions left
        navigate("/vendors");

        // Reload to get updated balances
        await loadTransactions();
      } else if (isMounted.current) {
        toast.error(response.data.error || "Failed to delete transaction");
      }
    } catch (error) {
      console.error("Error deleting transaction:", error);
      if (isMounted.current) {
        toast.error("Unable to delete transaction");
      }
    } finally {
      if (isMounted.current) {
        closeDeleteConfirm();
      }
    }
  };

  const handleEditTransaction = (transaction) => {
    navigate("/edit-vendor-transaction", { state: { vendor, transaction } });
  };

  const formatCurrency = (amount) => {
    return `₹${parseInt(amount).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const calculateTotals = () => {
    let totalCredit = 0;
    let totalDebit = 0;

    transactions.forEach((transaction) => {
      if (transaction.transaction_type === "1") {
        // IN = Credit (vendor paid us)
        totalCredit += parseFloat(transaction.amount || 0);
      } else {
        // OUT = Debit (we paid vendor)
        totalDebit += parseFloat(transaction.amount || 0);
      }
    });

    return { totalCredit, totalDebit };
  };

  if (!vendor) {
    return (
      <div
        className="container"
        style={{ textAlign: "center", padding: "40px" }}
      >
        <h3>No vendor selected</h3>
        <p>Please go back and select a vendor to view transactions.</p>
        <Link
          to="/vendors"
          style={{ color: "#0352a1", textDecoration: "none" }}
        >
          <i className="fas fa-arrow-left"></i> Back to Vendors
        </Link>
      </div>
    );
  }

  const { totalCredit, totalDebit } = calculateTotals();

  return (
    <div className="history-container">
      {/* Sticky Header Section */}
      <div
        className="sticky-filter"
        style={{ position: "fixed", top: "56px", width: "100%" }}
      >
        <div className="home-icon">
          <div className="section-home">
            {/* Vendor Info */}
            <div
              style={{
                backgroundColor: "#f0fff4",
                border: "1px solid #9ae6b4",
                borderRadius: "8px",
                padding: "10px",
                marginBottom: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div>
                  <h4 style={{ margin: "0", color: "#22543d" }}>
                    {vendor.vendor_firm_name}
                  </h4>
                  <p
                    style={{
                      margin: "5px 0 0 0",
                      color: "#4a5568",
                      fontSize: "14px",
                    }}
                  >
                    {vendor.vendor_name} • {vendor.vendor_mobile}
                  </p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div
                    style={{
                      color:
                        currentOutstanding > 0
                          ? "#dc2626"
                          : currentOutstanding < 0
                          ? "#f59e0b"
                          : "#10b981",
                      fontWeight: "bold",
                      fontSize: "18px",
                    }}
                  >
                    {formatCurrency(currentOutstanding)}
                  </div>
                  <div style={{ color: "#666", fontSize: "12px" }}>
                    Current Balance
                  </div>
                </div>
              </div>
            </div>

            {/* Date Filters */}
            <div
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-end",
                marginBottom: "10px",
              }}
            >
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "5px",
                    display: "block",
                  }}
                >
                  Start Date
                </label>
                <input
                  type="date"
                  name="from_date"
                  value={dateFilters.from_date}
                  onChange={handleDateFilterChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontSize: "12px",
                    color: "#666",
                    marginBottom: "5px",
                    display: "block",
                  }}
                >
                  End Date
                </label>
                <input
                  type="date"
                  name="to_date"
                  value={dateFilters.to_date}
                  onChange={handleDateFilterChange}
                  style={{
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "8px",
                    fontSize: "14px",
                  }}
                />
              </div>
              <button onClick={handleApplyFilter} className="search-btn">
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div
        className="content-area vendortransactions"
        style={{ paddingTop: "200px" }}
      >
        {/* Summary Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "10px",
            marginBottom: "15px",
            padding: "0 10px",
          }}
        >
          <div
            style={{
              backgroundColor: "#d1fae5",
              padding: "12px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "12px", color: "#065f46" }}>
              Total Credit
            </div>
            <div
              style={{ fontSize: "16px", fontWeight: "bold", color: "#065f46" }}
            >
              {formatCurrency(totalCredit)}
            </div>
          </div>
          <div
            style={{
              backgroundColor: "#fee2e2",
              padding: "12px",
              borderRadius: "8px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "12px", color: "#991b1b" }}>
              Total Debit
            </div>
            <div
              style={{ fontSize: "16px", fontWeight: "bold", color: "#991b1b" }}
            >
              {formatCurrency(totalDebit)}
            </div>
          </div>
        </div>

        {/* Transactions List */}
        <div className="home-vehicles">
          {loading ? (
            <div className="loading-vehicles">
              <div className="loading-spinner"></div>
              <div>Loading transactions...</div>
            </div>
          ) : transactions.length > 0 ? (
            <div style={{ padding: "0 10px" }}>
              <div
                style={{
                  backgroundColor: "#fff",
                  borderRadius: "10px",
                  overflow: "hidden",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  marginBottom: "20px",
                }}
              >
                {transactions.length > 0 && (
                  <div style={{ overflowX: "auto" }}>
                    <table
                      style={{ width: "100%", borderCollapse: "collapse" }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: "#0352a1",
                            color: "white",
                          }}
                        >
                          <th
                            style={{
                              padding: "12px 15px",
                              textAlign: "left",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Date
                          </th>
                          <th
                            style={{
                              padding: "12px 15px",
                              textAlign: "left",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Remark
                          </th>
                          <th
                            style={{
                              padding: "12px 15px",
                              textAlign: "left",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Credit
                          </th>
                          <th
                            style={{
                              padding: "12px 15px",
                              textAlign: "left",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Debit
                          </th>
                          <th
                            style={{
                              padding: "12px 15px",
                              textAlign: "left",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          >
                            Available Balance
                          </th>
                          <th
                            style={{
                              padding: "12px 15px",
                              textAlign: "left",
                              fontSize: "14px",
                              fontWeight: "600",
                            }}
                          ></th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((transaction, index) => (
                          <tr
                            key={transaction.transaction_id}
                            style={{
                              borderBottom: "1px solid #e2e8f0",
                              backgroundColor:
                                index % 2 === 0 ? "#fff" : "#f8f9fa",
                              cursor: "pointer",
                            }}
                            onClick={() => handleEditTransaction(transaction)}
                          >
                            <td
                              style={{
                                padding: "12px 15px",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatDate(transaction.transaction_date)}
                            </td>
                            <td
                              style={{
                                padding: "12px 15px",
                                fontSize: "14px",
                              }}
                            >
                              {transaction.remark || "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px 15px",
                                fontSize: "14px",
                                textAlign: "right",
                                color: "#10b981",
                                fontWeight: "600",
                              }}
                            >
                              {transaction.transaction_type === "1"
                                ? formatCurrency(parseFloat(transaction.amount))
                                : "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px 15px",
                                fontSize: "14px",
                                textAlign: "right",
                                color: "#dc2626",
                                fontWeight: "600",
                              }}
                            >
                              {transaction.transaction_type === "2"
                                ? formatCurrency(parseFloat(transaction.amount))
                                : "-"}
                            </td>
                            <td
                              style={{
                                padding: "12px 15px",
                                fontSize: "14px",
                                textAlign: "right",
                                fontWeight: "600",
                                color:
                                  parseFloat(transaction.available_balance) > 0
                                    ? "#dc2626"
                                    : parseFloat(
                                        transaction.available_balance
                                      ) < 0
                                    ? "#f59e0b"
                                    : "#10b981",
                              }}
                            >
                              {formatCurrency(
                                parseFloat(transaction.available_balance)
                              )}
                            </td>

                            <td
                              style={{
                                padding: "12px 15px",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {transaction.purchase_id ? (
                                <></>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteConfirm(
                                      transaction.transaction_id,
                                      transaction.amount
                                    );
                                  }}
                                  style={{
                                    backgroundColor: "#dc2626",
                                    color: "white",
                                    border: "none",
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                    fontSize: "13px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                    fontWeight: "500",
                                  }}
                                  title="Delete Transaction"
                                >
                                  <i className="fas fa-trash"></i>
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      {/* Footer with totals */}
                      {/* <tfoot>
                        <tr
                          style={{
                            backgroundColor: "#f1f5f9",
                            borderTop: "2px solid #0352a1",
                          }}
                        >
                          <td
                            colSpan="2"
                            style={{
                              padding: "12px 15px",
                              fontSize: "14px",
                              fontWeight: "600",
                              textAlign: "right",
                            }}
                          >
                            Total:
                          </td>
                          <td
                            style={{
                              padding: "12px 15px",
                              fontSize: "14px",
                              textAlign: "right",
                              color: "#10b981",
                              fontWeight: "600",
                            }}
                          >
                            {formatCurrency(totalCredit)}
                          </td>
                          <td
                            style={{
                              padding: "12px 15px",
                              fontSize: "14px",
                              textAlign: "right",
                              color: "#dc2626",
                              fontWeight: "600",
                            }}
                          >
                            {formatCurrency(totalDebit)}
                          </td>
                          <td
                            colSpan="2"
                            style={{
                              padding: "12px 15px",
                              fontSize: "14px",
                              fontWeight: "600",
                              textAlign: "center",
                              color: "#475569",
                            }}
                          ></td>
                        </tr>
                      </tfoot> */}
                    </table>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-vehicles section-home">
              <div className="no-vehicles-icon">📊</div>
              <div className="no-vehicles-title">No Transactions Found</div>
              <div className="no-vehicles-text">
                No transactions found for the selected date range. Add your
                first transaction for this vendor.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Add Button */}
      <button
        className="floating-add-btn"
        onClick={() =>
          navigate("/add-vendor-transaction", { state: { vendor } })
        }
        title="Add New Transaction"
      >
        <i className="fas fa-plus"></i>
      </button>

      {/* Delete Confirmation Modal */}
      {deleteConfirm.isOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "12px",
              width: "100%",
              maxWidth: "350px",
              padding: "24px",
              textAlign: "center",
            }}
          >
            <div
              style={{
                width: "60px",
                height: "60px",
                margin: "0 auto 20px",
                backgroundColor: "#fee2e2",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <i
                className="fas fa-exclamation-triangle"
                style={{ fontSize: "24px", color: "#dc2626" }}
              ></i>
            </div>

            <h3
              style={{
                margin: "0 0 12px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#1f2937",
              }}
            >
              Delete Transaction
            </h3>
            <p
              style={{
                margin: "0 0 24px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              Are you sure you want to delete this transaction of{" "}
              {formatCurrency(deleteConfirm.transactionAmount)}? This will
              affect the vendor's outstanding balance. This action cannot be
              undone.
            </p>

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={closeDeleteConfirm}
                style={{
                  flex: 1,
                  padding: "12px",
                  border: "2px solid #e2e8f0",
                  background: "white",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#dc2626",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .search-btn {
          background: #0352a1;
          color: white;
          border: none;
          border-radius: 12px;
          padding: 12px 15px !important;
          min-width: 40px;
          cursor: pointer !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 768px) {
          table {
            font-size: 12px;
          }

          th,
          td {
            padding: 8px 10px !important;
          }

          button {
            padding: 4px 8px !important;
            font-size: 11px !important;
          }
        }

        @media (max-width: 480px) {
          table {
            font-size: 11px;
          }

          th,
          td {
            padding: 6px 8px !important;
          }
        }
        .history-container {
          min-height: 100vh;
          background: white;
          padding: 0;
          position: relative;
        }

        .sticky-filter {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 8px 0;
          border-bottom: 1px solid #e2e8f0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        .content-area {
          padding-top: 180px;
          padding-bottom: 80px;
        }

        .home-icon .section-home {
          padding: 8px 12px 12px 12px;
        }
        .home-icon {
          padding-left: 0px;
          padding-right: 0px;
        }

        .home-vehicles {
          padding: 20px 10px;
        }

        .loading-vehicles {
          text-align: center;
          padding: 40px 20px;
          color: #666;
        }

        .loading-spinner {
          width: 30px;
          height: 30px;
          border: 3px solid #f3f3f3;
          border-top: 3px solid #0352a1;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 15px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .vehicles-list {
          display: grid;
          gap: 15px;
        }

        .vehicle-card {
          background: #f8f9fa;
          border-radius: 12px;
          padding: 16px;
          transition: all 0.3s ease;
        }

        .vehicle-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .vehicle-header {
          width: 100%;
        }

        .vehicle-header table {
          width: 100%;
          border-collapse: collapse;
        }

        .vehicle-header td {
          padding: 4px 0;
          vertical-align: top;
        }

        .no-vehicles {
          text-align: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 12px;
          margin: 20px 0;
        }

        .no-vehicles-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }

        .no-vehicles-title {
          font-size: 18px;
          font-weight: 600;
          color: #2c3e50;
          margin-bottom: 8px;
        }

        .no-vehicles-text {
          color: #666;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .floating-add-btn {
          position: fixed;
          bottom: 80px;
          right: 20px;
          width: 60px;
          height: 60px;
          background: #0352a1;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          cursor: pointer;
          box-shadow: 0 4px 12px rgba(3, 82, 161, 0.3);
          z-index: 99;
          transition: all 0.3s ease;
          border: none;
        }

        .floating-add-btn:hover {
          transform: scale(1.1);
          box-shadow: 0 6px 16px rgba(3, 82, 161, 0.4);
        }

        .floating-add-btn:active {
          transform: scale(0.95);
        }

        @media (max-width: 768px) {
          .content-area {
            padding-top: 200px;
          }

          .home-vehicles {
            padding: 0px;
          }

          .vehicle-card {
            padding: 12px;
          }

          .vehicle-header td {
            font-size: 14px;
          }

          .sticky-filter {
            padding: 6px 0;
          }

          .floating-add-btn {
            bottom: 70px;
            right: 15px;
            width: 56px;
            height: 56px;
            font-size: 22px !important;
          }
        }

        @media (max-width: 480px) {
          .content-area {
            padding-top: 190px;
          }

          .sticky-filter {
            padding: 4px 0;
          }

          .floating-add-btn {
            bottom: 60px;
            right: 12px;
            width: 52px;
            height: 52px;
            font-size: 20px;
          }
        }
      `}</style>
    </div>
  );
};

export default VendorTransactions;
